import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Subject, delay, firstValueFrom } from 'rxjs';
import { ErrorService } from '@app/pages/error/error.service';
import { SessionStorageService } from '@app/sessionStorage.service';
import { OrderJSON } from '@app/@shared/orderjson.model';

export class RoutePage {
  page?: string;
  nextRoute?: string;
  previousRoute?: string;
}

export interface FlowConfiguration {
  pages: string[];
  totalSteps: number;
}

@Injectable({
  providedIn: 'root',
})
export class HeaderService {
  private $existingUser = new BehaviorSubject(false);
  public _existingUser = this.$existingUser.asObservable();
  private _pageConfiguration = new BehaviorSubject({
    currentPage: '',
    nextPage: '',
    previousPage: '',
    subTitle: '',
    title: 'Choose your path',
    progress: 0,
    step: 1,
  });

  // Define flow configurations for different subscription types
  private flowConfigurations: { [key: string]: FlowConfiguration } = {
    'POS+': {
      pages: [
        '/subscription',
        '/software-plans',
        '/business-information',
        '/software-features',
        '/equipment-services',
        '/summary',
        '/payment-plans',
        '/agreements',
        '/success'
      ],
      totalSteps: 8
    },
    'NRSPAY': {
      pages: [
        '/subscription',
        '/nrspay-plans',
        '/business-information',
        '/business-information2',
        '/bank-page',
        '/summary',
        '/payment-plans',
        '/agreements',
        '/success'
      ],
      totalSteps: 8
    },
    'DEFAULT': {
      pages: [
        '/subscription',
        '/software-plans',
        '/nrspay-plans',
        '/business-information',
        '/business-information2',
        '/bank-page',
        '/software-features',
        '/equipment-services',
        '/summary',
        '/payment-plans',
        '/agreements',
        '/success'
      ],
      totalSteps: 11
    }
  };

  // Maps a flow route to its stepper label. Routes without an entry here
  // (e.g. /agreements, /success) are intentionally omitted from the stepper.
  private routeStepLabelsMap: { [route: string]: string } = {
    '/subscription': 'SELECT POS',
    '/software-plans': 'SOFTWARE',
    '/nrspay-plans': 'NRS PAY',
    '/business-information': 'ACCOUNT',
    '/business-information2': 'DETAILS',
    '/bank-page': 'BANK SETUP',
    '/software-features': 'FEATURES',
    '/equipment-services': 'EQUIPMENT',
    '/summary': 'SUMMARY',
    '/payment-plans': 'PAYMENT',
  };

  private $isPantherTablet = new BehaviorSubject(false);
  public isPantherTablet$ = this.$isPantherTablet.asObservable();

  public onSubscriptionChange: Subject<String> = new Subject<String>();
  
  get isPantherTablet(): boolean {
    return this.$isPantherTablet.value;
  }

  set isPantherTablet(value: boolean) {
    this.$isPantherTablet.next(value);
  }

  public previousRoute = '';
  public nextPage = '';

  public pageConfiguration$ = this._pageConfiguration.asObservable();

  get currentPageConfiguration() {
    return this._pageConfiguration.value;
  }

  /**
   * Get the total steps for the current flow
   */
  get totalStepsForCurrentFlow(): number {
    const orderData = this.getOrderDataFromSession();
    const flowConfig = this.getFlowConfiguration(orderData?.subscriptionType);
    return flowConfig.totalSteps;
  }

  /**
   * Get the ordered stepper labels for the current flow. Only routes that
   * have a mapped label are included, so pages like /agreements and /success
   * are omitted from the stepper.
   */
  get stepLabelsForCurrentFlow(): string[] {
    const orderData = this.getOrderDataFromSession();
    const flowConfig = this.getFlowConfiguration(orderData?.subscriptionType);
    return this.getLabeledRoutesForFlow(flowConfig).map((route) => this.routeStepLabelsMap[route]);
  }

  /**
   * Returns the flow route that corresponds to a visible stepper label index.
   * Returns null if the index is out of range.
   */
  public getRouteForStepLabelIndex(index: number): string | null {
    const orderData = this.getOrderDataFromSession();
    const flowConfig = this.getFlowConfiguration(orderData?.subscriptionType);
    const labeledRoutes = this.getLabeledRoutesForFlow(flowConfig);
    if (index < 0 || index >= labeledRoutes.length) {
      return null;
    }
    return labeledRoutes[index];
  }

  private getLabeledRoutesForFlow(flowConfig: FlowConfiguration): string[] {
    return flowConfig.pages.filter((route) => this.routeStepLabelsMap[route] !== undefined);
  }

  /**
   * Returns the public route-step label map so individual pages can resolve
   * stepper metadata (e.g., mark the summary step completed) without duplicating
   * flow definitions.
   */
  get routeStepLabels(): { [route: string]: string } {
    return this.routeStepLabelsMap;
  }

  public getFlowConfiguration(subscriptionType?: string): FlowConfiguration {
    if (!subscriptionType) {
      return this.flowConfigurations['DEFAULT'];
    }

    // Map different subscription types to flow configurations
    if (subscriptionType === 'POS+') {
      return this.flowConfigurations['POS+'];
    } else if (subscriptionType === 'NRSPAY') {
      return this.flowConfigurations['NRSPAY'];
    } else if (subscriptionType.includes('PANTHER') || 
               subscriptionType.includes('POS+NRSPAY') || 
               subscriptionType.includes('Cash Discount')) {
      // For POS+NRSPAY, PANTHER, Cash Discount etc., use the default full flow
      return this.flowConfigurations['DEFAULT'];
    } else {
      // Default to full flow for unknown types
      return this.flowConfigurations['DEFAULT'];
    }
  }

  /**
   * Persists the current ramJson to the backend so metadata such as
   * maxVisitedStepIndex survives login on a different browser/session.
   * Only fires when the order already has a Guid.
   */
  public persistOrderMetadata(ramJson: OrderJSON): void {
    if (!ramJson || !ramJson.Guid) {
      return;
    }
    const headers = new HttpHeaders()
      .set('enctype', 'multipart/form-data')
      .set('Accept', 'application/json');
    const formData = new FormData();
    formData.append('model', JSON.stringify(ramJson));
    // this.httpClient.put('/orders', formData, { headers }).subscribe({
    //   next: () => console.log('Stepper metadata persisted to order'),
    //   error: (err: any) => console.error('Failed to persist stepper metadata', err),
    // });
  }

  private msg = new Subject<boolean>();
  isHeader = this.msg.asObservable();

  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private errorService: ErrorService,
    private sessionStorageService: SessionStorageService
  ) {

    this.onSubscriptionChange.pipe(delay(0)).subscribe((subscription) => {
        this.setPageConfigurationForCurrentRoute(this.currentPageConfiguration.currentPage);
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
       this.setPageConfigurationForCurrentRoute(event.url);
      }
    });
  }

  setPageConfigurationForCurrentRoute(url: string) {
     // check if the user is existing user
        let jwttoken = sessionStorage.getItem('token');

        if (jwttoken && jwttoken.length > 0) {
          this.existingUser = true;
        } else {
          this.existingUser = false;
        }

        const cleanUrl = url.split('?')[0];
        
        // Get current subscription type for dynamic configuration
        const orderData = this.getOrderDataFromSession();
        const flowConfig = this.getFlowConfiguration(orderData?.subscriptionType);
        const { step, progress, nextPage, previousPage } = this.calculateDynamicPageInfo(cleanUrl, flowConfig);

        switch (cleanUrl) {
          case '/recover-password':
          case '/create-password':
          case '/login': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage: '',
              previousPage: '',
              subTitle: '',
              title: '',
              progress: 0,
              step: 0,
            };
            break;
          }
          case '/subscription': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage,
              previousPage: '',
              subTitle: "Let's build your bundle",
              title: 'Choose your POS hardware and payment processing',
              progress,
              step,
            };
            break;
          }
          case '/software-plans': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage,
              previousPage,
              subTitle:'Maximize your POS system',
              title:'Choose your software',
              progress,
              step,
            };
            break;
          }
          case '/nrspay-plans': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage,
              previousPage,
              subTitle:'Get the most out of credit card processing',
              title: 'Choose your <img height="20" src="assets/images/NRSPay.png"/> plan',
              progress,
              step,
            };
            break;
          }
          case '/business-information': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage,
              previousPage,
              subTitle: "Let's set up your account",
              title: 'Enter your <u>account</u> details',
              progress,
              step,
            };
            break;
          }
          case '/email-verification': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage: '/email-verification',
              previousPage: '/email-verification',
              subTitle: "Let's set up your account",
              title: 'Email Verification',
              progress: 80,
              step: 5,
            };
            break;
          }
          case '/business-information2': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage,
              previousPage,
              subTitle: "Let's set up your account",
              title: 'Enter your <u>account</u> details',
              progress,
              step,
            };
            break;
          }
          case '/bank-page': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage,
              previousPage,
              subTitle: "Let's set up your account",
              title: 'Enter your <u>Bank</u> details',
              progress,
              step,
            };
            break;
          }
          case '/software-features': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage,
              previousPage,
              subTitle: 'Boost your business with premium features',
              title: 'Choose your features',
              progress,
              step,
            };
            break;
          }
          case '/equipment-services': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage,
              previousPage,
              subTitle: 'Get exceptional hardware for your service',
              title: 'Choose your equipment',
              progress,
              step,
            };
            break;
          }
          case '/summary': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage,
              previousPage,
              subTitle: 'Summary of your order',
              title: 'Order Summary',
              progress,
              step,
            };
            break;
          }
          case '/payment-plans': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage,
              previousPage,
              subTitle: 'Just a few more boxes to tick',
              title: 'Choose your payment method & pay',
              progress,
              step,
            };
            break;
          }
          case '/agreements': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage,
              previousPage,
              subTitle: "Let's set up your account",
              title: 'Final step',
              progress,
              step,
            };
            break;
          }
          case '/success': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage: '/',
              previousPage: this.pageConfiguration.currentPage,
              subTitle: '',
              title: 'Success',
              progress: progress,
              step: step,
            };
            console.log('progress', progress);
            break;
          }
          case '/account-portal': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage: '',
              previousPage: '',
              subTitle: '',
              title: '',
              progress: 0,
              step: 0,
            };
            break;
          }
          case '/wizard': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage: '/',
              previousPage: this.pageConfiguration.currentPage,
              subTitle: '',
              title: '',
              progress: 0,
              step: 0,
            };
            break;
          }
          case '/wizard/best-fit': {
            this.pageConfiguration = {
              currentPage: url,
              nextPage: '/',
              previousPage: this.pageConfiguration.currentPage,
              subTitle: '',
              title: '',
              progress: 0,
              step: 0,
            };
            break;
          }
        }
  }

  disableHeader(value: boolean) {
    this.msg.next(value);
  }

  set existingUser(value: boolean) {
    this.$existingUser.next(value);
  }

  get existingUser() {
    return this.$existingUser.value;
  }

  set pageConfiguration(value: {
    currentPage: string;
    nextPage: string;
    previousPage: string;
    subTitle: string;
    title: string;
    progress: number;
    step: number;
  }) {
    sessionStorage.setItem('routeInfo', JSON.stringify(value));
    this._pageConfiguration.next(value);
  }

  get pageConfiguration() {
    let pageInformation = sessionStorage.getItem('routeInfo');
    if (pageInformation) {
      return JSON.parse(pageInformation);
    } else {
      return this._pageConfiguration.value;
    }
  }

  public preNavigationHooks: Array<Function> = [];

  /**
   * Optional per-page unsaved-changes check. Each wizard page sets this in its
   * ngOnInit (alongside preNavigationHooks) to report whether it currently has
   * edits the user would lose by navigating away. The stepper uses it to decide
   * whether to prompt before jumping to another step.
   */
  public dirtyCheck: (() => boolean) | null = null;

  /**
   * Safely evaluates the active page's dirty check. Any error is treated as
   * "not dirty" so a faulty check can never block navigation.
   */
  public isCurrentPageDirty(): boolean {
    try {
      return this.dirtyCheck ? !!this.dirtyCheck() : false;
    } catch (error) {
      console.error('dirtyCheck failed', error);
      return false;
    }
  }

  public async onNext(callback: Function) {
    try {
      // Create an array of promises for all hooks
      const hookPromises = this.preNavigationHooks.map(async (hook: Function) => {
        try {
          await hook(); // Wait for each hook to complete
        } catch (error) {
          console.log(error);
          throw error; // If any hook fails, throw the error to be caught below
        }
      });
      // Wait for all hooks to complete
      await Promise.all(hookPromises);
      await firstValueFrom(this.errorService.errorMessage$);

      // Clear hooks and call the callback after all hooks are successfully completed
      this.preNavigationHooks = [];
      callback();
      return null;
    } catch (error) {
      console.log('ERROR', error);
      return error;
    }
  }

  /**
   * Get order data from session storage
   */
  private getOrderDataFromSession(): OrderJSON | null {
    try {
      let orderData = this.sessionStorageService.getAllData();
                        
      if (!orderData) {
        const subscriptionType = sessionStorage.getItem('subscriptionType');
        if (subscriptionType) {
          return { subscriptionType } as OrderJSON;
        }
        return null;
      }
      
      // If we have ramJson, use it directly
      if (orderData && orderData.subscriptionType) {
        return orderData;
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing order data from session:', error);
      return null;
    }
  }


  /**
   * Calculate dynamic page information based on current page and flow configuration
   */
  private calculateDynamicPageInfo(currentPage: string, flowConfig: FlowConfiguration): {
    step: number;
    progress: number;
    nextPage: string;
    previousPage: string;
  } {
    const pages = flowConfig.pages;
    const currentIndex = pages.indexOf(currentPage);
    
    if (currentIndex === -1) {
      // Page not found in flow, return defaults
      return {
        step: 1,
        progress: 0,
        nextPage: '',
        previousPage: ''
      };
    }

    const step = currentIndex + 1;
    const progress = Math.round((step / flowConfig.totalSteps) * 100);
    const nextPage = currentIndex < pages.length - 1 ? pages[currentIndex + 1] : '';
    const previousPage = currentIndex > 0 ? pages[currentIndex - 1] : '/';

    return {
      step,
      progress,
      nextPage,
      previousPage
    };
  }
}
